import Header from './components/Header';
import Footer from './components/Footer';
import HeroSection from './components/HeroSection';
import {
  mediaItems,
  featureItems,
  contactInfo,
  appInfo,
  indexItems,
  topicItems,
  carouselItems,
  cardItems,
  commentItems,
  updateItems,
  rankingItems,
  aboutContent,
  faqItems,
} from '@/mocks/home';

function SectionWrapper({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <section id={id} className="w-full py-12 md:py-16 px-4 md:px-6">
      <div className="max-w-[1280px] mx-auto">{children}</div>
    </section>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-8 md:mb-10 text-center">
      <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading">{title}</h2>
      {subtitle && <p className="mt-2 text-sm text-foreground-500">{subtitle}</p>}
    </div>
  );
}

const tagColors: Record<string, string> = {
  '完更': 'bg-accent-500/10 text-accent-300 border-accent-500/20',
  '公告': 'bg-secondary-500/10 text-secondary-300 border-secondary-500/20',
  '独家': 'bg-primary-500/10 text-primary-300 border-primary-500/20',
  '活动': 'bg-accent-500/10 text-accent-300 border-accent-500/20',
  '预告': 'bg-secondary-500/10 text-secondary-300 border-secondary-500/20',
  '优化': 'bg-foreground-500/10 text-foreground-400 border-foreground-500/20',
  '里程碑': 'bg-primary-500/10 text-primary-300 border-primary-500/20',
};

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <HeroSection />

        {/* #media - 热门剧集 */}
        <SectionWrapper id="media">
          <SectionTitle title="热门剧集" subtitle="全网热播电视剧，高清免费在线观看" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {mediaItems.map((item) => (
              <div key={item.id} className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-500/30 transition-all cursor-pointer">
                <div className="relative aspect-video overflow-hidden">
                  <img src={item.cover} alt={item.title} title={`${item.title} - 成全影视高清在线观看`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-0.5 rounded-md bg-background-900/70 backdrop-blur-sm text-xs text-foreground-200 whitespace-nowrap">{item.tag}</span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-0.5 rounded-md bg-primary-500/80 text-xs text-foreground-50 font-medium whitespace-nowrap">{item.heat}</span>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-background-50/80 via-transparent to-transparent"></div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground-100 group-hover:text-primary-400 transition-colors truncate">{item.title}</h3>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-foreground-500">{item.episodes}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-accent-500/10 text-accent-300 whitespace-nowrap">{item.status}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionWrapper>

        {/* #features - 平台优势 */}
        <SectionWrapper id="features">
          <div className="bg-background-100 rounded-2xl border border-background-200/60 p-8 md:p-12">
            <SectionTitle title="为什么选择成全影视" subtitle="三大核心优势，让追剧更轻松" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {featureItems.map((item) => (
                <div key={item.id} className="text-center md:text-left p-4 md:p-5 rounded-xl bg-background-50/50 border border-background-200/40">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 mb-4 mx-auto md:mx-0">
                    <i className={`${item.icon} text-xl text-primary-400`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-100 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </SectionWrapper>

        {/* #contact - 合作联系 */}
        <SectionWrapper id="contact">
          <div className="bg-background-100 rounded-2xl border border-background-200/60 p-8 md:p-12 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-500/10 mx-auto mb-6">
              <i className="ri-mail-send-line text-2xl text-accent-400"></i>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-3">{contactInfo.title}</h2>
            <p className="text-sm text-foreground-500 leading-relaxed max-w-[560px] mx-auto mb-6">{contactInfo.desc}</p>
            <a href="#" className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-background-950 font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap text-sm">
              <i className="ri-chat-3-line text-lg"></i>
              立即联系
            </a>
          </div>
        </SectionWrapper>

        {/* #app - 移动端入口 */}
        <SectionWrapper id="app">
          <div className="bg-background-100 rounded-2xl border border-background-200/60 p-8 md:p-12">
            <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
              <div className="flex-1 text-center lg:text-left">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-3">{appInfo.title}</h2>
                <p className="text-sm text-foreground-500 leading-relaxed mb-6">{appInfo.desc}</p>
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
                  {appInfo.features.map((f) => (
                    <span key={f} className="px-3 py-1.5 rounded-full bg-secondary-500/10 border border-secondary-500/20 text-xs text-secondary-300 whitespace-nowrap">{f}</span>
                  ))}
                </div>
              </div>
              <div className="flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Smartphone%20screen%20showing%20Chinese%20drama%20streaming%20app%20interface%20with%20drama%20poster%20thumbnails%2C%20dark%20theme%20UI%2C%20modern%20mobile%20design%2C%20clean%20layout%2C%20cinematic%20entertainment%20app%20mockup%2C%20minimalist%20dark%20background&width=360&height=640&seq=app-mobile&orientation=portrait"
                  alt="成全影视手机端追剧界面"
                  className="w-[240px] md:w-[280px] h-auto rounded-2xl"
                  title="成全影视移动端 - 随时随地免费追剧"
                />
              </div>
            </div>
          </div>
        </SectionWrapper>

        {/* #index - 专题合集 */}
        <SectionWrapper id="index">
          <SectionTitle title="专题合集" subtitle="按题材发现好剧，总有一部适合你" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 md:gap-4">
            {indexItems.map((item) => (
              <a key={item.id} href="#media" className="group flex items-center justify-between p-4 md:p-5 bg-background-100 rounded-xl border border-background-200/60 hover:border-primary-500/30 transition-all cursor-pointer">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                    <i className="ri-folder-video-line text-lg text-primary-400"></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-foreground-100 truncate">{item.title}</h3>
                    <p className="text-xs text-foreground-500">{item.count}部剧集</p>
                  </div>
                </div>
                <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs whitespace-nowrap ml-2 ${item.tag === '热门' ? 'bg-primary-500/10 text-primary-300' : item.tag === '推荐' ? 'bg-accent-500/10 text-accent-300' : item.tag === '新番' ? 'bg-secondary-500/10 text-secondary-300' : 'bg-foreground-500/10 text-foreground-400'}`}>
                  {item.tag}
                </span>
              </a>
            ))}
          </div>
        </SectionWrapper>

        {/* #topics - 热门话题 */}
        <SectionWrapper id="topics">
          <div className="bg-background-100 rounded-2xl border border-background-200/60 p-6 md:p-8">
            <h2 className="text-lg md:text-xl font-bold text-foreground-50 font-heading mb-6 text-center">热门话题</h2>
            <div className="flex flex-wrap gap-2 md:gap-3 justify-center">
              {topicItems.map((item) => (
                <span key={item.id} className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-background-50 border border-background-300/60 text-sm text-foreground-300 hover:text-primary-400 hover:border-primary-500/30 transition-all cursor-pointer whitespace-nowrap">
                  {item.name}
                  <span className="text-xs text-foreground-600">{item.heat}</span>
                </span>
              ))}
            </div>
          </div>
        </SectionWrapper>

        {/* #carousel - 焦点推荐（全部静态展示） */}
        <SectionWrapper id="carousel">
          <SectionTitle title="焦点推荐" subtitle="本周必看热剧，一次完整呈现" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {carouselItems.slice(0, 3).map((item) => (
              <div key={item.id} className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-500/30 transition-all cursor-pointer">
                <div className="aspect-video overflow-hidden">
                  <img src={item.cover} alt={item.title} title={`${item.title} - 成全影视焦点推荐`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded-md bg-primary-500/10 text-xs text-primary-300 whitespace-nowrap">{item.tag}</span>
                    <span className="text-xs text-accent-400 font-medium whitespace-nowrap">{item.rating}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-100 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 mt-4 md:mt-5">
            {carouselItems.slice(3, 5).map((item) => (
              <div key={item.id} className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-500/30 transition-all cursor-pointer flex flex-col sm:flex-row">
                <div className="sm:w-[200px] aspect-video sm:aspect-auto overflow-hidden flex-shrink-0">
                  <img src={item.cover} alt={item.title} title={`${item.title} - 成全影视焦点推荐`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 rounded-md bg-primary-500/10 text-xs text-primary-300 whitespace-nowrap">{item.tag}</span>
                    <span className="text-xs text-accent-400 font-medium whitespace-nowrap">{item.rating}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-100 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionWrapper>

        {/* #cards - 精选推荐 */}
        <SectionWrapper id="cards">
          <SectionTitle title="精选推荐" subtitle="编辑严选，品质好剧不容错过" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {cardItems.map((item) => (
              <div key={item.id} className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-500/30 transition-all cursor-pointer">
                <div className="aspect-[2/3] overflow-hidden">
                  <img src={item.cover} alt={item.title} title={`${item.title} - 成全影视精选推荐`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <span className="inline-block px-2 py-0.5 rounded-md bg-accent-500/10 text-xs text-accent-300 mb-2 whitespace-nowrap">{item.tag}</span>
                  <h3 className="text-sm font-semibold text-foreground-100 mb-1 truncate">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionWrapper>

        {/* #comments - 观众热议 */}
        <SectionWrapper id="comments">
          <SectionTitle title="观众热议" subtitle="真实追剧体验，看看大家都在聊什么" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {commentItems.map((item, idx) => (
              <div key={item.id} className={`bg-background-100 rounded-xl border border-background-200/60 p-5 ${idx >= 3 ? 'lg:col-span-1 md:col-span-2 lg:col-auto' : ''}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-secondary-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-secondary-300">{item.name.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-100">{item.name}</div>
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <span>{item.time}</span>
                      <span>· 在看《{item.show}》</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-foreground-400 leading-relaxed mb-3">{item.content}</p>
                <div className="flex items-center gap-4 text-xs text-foreground-600">
                  <button className="flex items-center gap-1 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-thumb-up-line"></i>
                    {item.likes.toLocaleString()}
                  </button>
                  <button className="flex items-center gap-1 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-chat-1-line"></i>
                    回复
                  </button>
                </div>
              </div>
            ))}
          </div>
        </SectionWrapper>

        {/* #updates - 最新动态 */}
        <SectionWrapper id="updates">
          <SectionTitle title="最新动态" subtitle="平台更新与剧集上线一手掌握" />
          <div className="bg-background-100 rounded-2xl border border-background-200/60 p-5 md:p-8">
            <div className="space-y-0">
              {updateItems.map((item, idx) => (
                <div key={item.id} className={`flex items-start gap-4 py-3.5 ${idx < updateItems.length - 1 ? 'border-b border-background-200/40' : ''}`}>
                  <span className="text-xs text-foreground-500 font-mono whitespace-nowrap pt-0.5">{item.date}</span>
                  <span className={`flex-shrink-0 px-2 py-0.5 rounded-md text-xs border whitespace-nowrap ${tagColors[item.tag] || 'bg-foreground-500/10 text-foreground-400 border-foreground-500/20'}`}>
                    {item.tag}
                  </span>
                  <p className="text-sm text-foreground-300 leading-relaxed flex-1">{item.title}</p>
                </div>
              ))}
            </div>
          </div>
        </SectionWrapper>

        {/* #ranking - 热播排行 */}
        <SectionWrapper id="ranking">
          <SectionTitle title="热播排行" subtitle="全网热度实时排行，追剧风向标" />
          <div className="bg-background-100 rounded-2xl border border-background-200/60 p-5 md:p-8">
            <div className="space-y-0">
              {rankingItems.map((item, idx) => (
                <div key={item.id} className={`flex items-center gap-4 py-3.5 ${idx < rankingItems.length - 1 ? 'border-b border-background-200/40' : ''}`}>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-bold flex-shrink-0 ${
                    item.rank === 1 ? 'bg-primary-500 text-foreground-50' :
                    item.rank === 2 ? 'bg-accent-500 text-background-950' :
                    item.rank === 3 ? 'bg-secondary-500 text-foreground-50' :
                    'bg-background-200 text-foreground-400'
                  }`}>
                    {item.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-100 truncate">{item.title}</h3>
                    <span className="text-xs text-foreground-500">{item.tag}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-sm font-bold text-accent-400 whitespace-nowrap">{item.heat}</span>
                    <span className="text-xs">
                      {item.trend === 'up' ? <i className="ri-arrow-up-line text-accent-400"></i> :
                       item.trend === 'down' ? <i className="ri-arrow-down-line text-foreground-600"></i> :
                       <i className="ri-subtract-line text-foreground-600"></i>}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </SectionWrapper>

        {/* #about - 关于我们 */}
        <SectionWrapper id="about">
          <div className="bg-background-100 rounded-2xl border border-background-200/60 p-8 md:p-12">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-50 font-heading mb-6 text-center">{aboutContent.title}</h2>
            <div className="max-w-[780px] mx-auto space-y-4 mb-10">
              {aboutContent.paragraphs.map((p, idx) => (
                <p key={idx} className="text-sm text-foreground-400 leading-relaxed">{p}</p>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {aboutContent.stats.map((s) => (
                <div key={s.label} className="text-center p-4 rounded-xl bg-background-50/50 border border-background-200/40">
                  <div className="text-xl md:text-2xl font-bold text-primary-400 font-heading whitespace-nowrap">{s.value}</div>
                  <div className="text-xs text-foreground-500 mt-1 whitespace-nowrap">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </SectionWrapper>

        {/* #faq - 常见问题 */}
        <SectionWrapper id="faq">
          <SectionTitle title="常见问题" subtitle="关于成全影视，你想知道的都在这里" />
          <div className="max-w-[780px] mx-auto space-y-3">
            {faqItems.map((item) => (
              <div key={item.id} className="bg-background-100 rounded-xl border border-background-200/60 p-5 md:p-6">
                <h3 className="text-sm md:text-base font-semibold text-foreground-100 mb-3 flex items-start gap-3">
                  <span className="text-primary-400 flex-shrink-0">Q</span>
                  {item.question}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed pl-6">{item.answer}</p>
              </div>
            ))}
          </div>
        </SectionWrapper>
      </main>
      <Footer />
    </div>
  );
}